/**
 * The joint-operations overview: every AOR at once, worst first.
 *
 * The single fleet grade answers "how is my fleet"; this answers the question a
 * commander over several areas asks first — "which area do I look at". It joins
 * the server's per-aircraft health grades with where each aircraft is, buckets
 * them by AOR, and rolls each area up worst-governs, so a critical area is loud
 * however many healthy aircraft sit beside the one in trouble.
 *
 * Each area is a door: clicking one switches the working AOR to it, the way the
 * globe's pills do.
 */

import { useEffect, useState } from 'react';

import { getFleetHealth, type HealthGrade } from '@/api/fleetHealth';
import { jocRollup, type AorRollup, type GradedAircraft } from '@/features/fleet/jocAggregate';
import { useDroneStore } from '@/stores/droneStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';
const WHITE = '#f4f4f5';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

const GRADE_TONE: Record<HealthGrade, string> = {
  healthy: OK,
  degraded: WARN,
  critical: DANGER,
  unknown: MUTED,
};

const REFRESH_MS = 2000;

export function JocPanel() {
  const [grades, setGrades] = useState<Record<string, HealthGrade>>({});
  const setAor = useWorkspaceStore((s) => s.setAor);
  const currentAor = useWorkspaceStore((s) => s.aor);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getFleetHealth().then((result) => {
        if (cancelled || !result.ok) return;
        const next: Record<string, HealthGrade> = {};
        for (const a of result.data.aircraft) next[a.droneId] = a.grade;
        setGrades(next);
      });
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Join grades with live positions from the store. A grade with no known
  // position still counts — it lands in the unassigned bucket rather than
  // vanishing from the fleet total.
  const drones = useDroneStore((s) => s.drones);
  const aircraft: GradedAircraft[] = [];
  for (const [droneId, grade] of Object.entries(grades)) {
    const drone = drones.get(droneId);
    aircraft.push({ droneId, grade, lat: drone?.scalars.lat, lon: drone?.scalars.lon });
  }
  const rollup = jocRollup(aircraft);

  if (rollup.length === 0) return null;

  return (
    <div
      className="rounded-[18px] border p-[16px_18px]"
      style={{ background: CARD, borderColor: 'rgba(255,255,255,0.08)' }}
    >
      <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
        JOINT OPERATIONS · ALL AREAS
      </span>
      <div className="mt-3 flex flex-col gap-1.5">
        {rollup.map((area) => (
          <AreaRow
            key={area.aorId}
            area={area}
            current={area.aorId === currentAor}
            onEnter={() => setAor(area.aorId)}
          />
        ))}
      </div>
    </div>
  );
}

function AreaRow({
  area,
  current,
  onEnter,
}: {
  area: AorRollup;
  current: boolean;
  onEnter: () => void;
}) {
  const tone = GRADE_TONE[area.worstGrade];
  const clickable = area.aorId !== 'UNASSIGNED';
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onEnter}
      className="flex items-center gap-2 rounded-[10px] px-2 py-1.5 text-left disabled:cursor-default"
      style={{
        background: current ? 'rgba(255,255,255,0.06)' : 'transparent',
        outline: current ? '1px solid rgba(255,255,255,0.12)' : 'none',
      }}
    >
      <span className="size-2 rounded-full" style={{ background: tone }} aria-hidden />
      <span className="tnum text-[12px]" style={{ color: WHITE }}>
        {area.aorId}
      </span>
      <span className="label-mono text-[8px]" style={{ color: tone }}>
        {area.worstGrade.toUpperCase()}
      </span>
      <span className="tnum ml-auto text-[11px]" style={{ color: MUTED }}>
        {area.counts.critical > 0 ? (
          <span style={{ color: DANGER }}>{area.counts.critical}✕ </span>
        ) : null}
        {area.total} AC
      </span>
    </button>
  );
}
